import { useState, useCallback } from 'react';
import {
  Box,
  Button,
  LinearProgress,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import type { MessageDefinition, CoordinateConfig, MessageInstance, MessageEvent } from '@k-ucap/shared';
import { useBulkMessageWorker } from '@/hooks/useBulkMessageWorker.js';
import { useAuthStore } from '@/stores/useAuthStore.js';
import { useRepositories } from '@/infrastructure/repositories/useRepositories.js';
import { createMessageValidationService } from '@/domain/message/MessageValidationService.js';
import { PreviewMessageEncoder } from '@/domain/message/PreviewMessageEncoder.js';
import { getGlobalVirtualMessageAdapter } from '@/infrastructure/adapters/VirtualMessageAdapter.js';

interface BulkGeneratorProps {
  messageDefinition: MessageDefinition;
  payload: Record<string, unknown>;
}

const defaultCoordinate: CoordinateConfig = {
  mode: 'CENTER_RADIUS',
  centerLat: 37.389,
  centerLon: 126.647,
  radiusKm: 10,
};

export const BulkGenerator = ({ messageDefinition, payload }: BulkGeneratorProps) => {
  const [count, setCount] = useState<number>(10000);
  const [progress, setProgress] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const selectedProject = useAuthStore((s) => s.selectedProject);
  const userProfile = useAuthStore((s) => s.userProfile);
  const { messageEventRepository } = useRepositories();

  const processChunk = useCallback(
    async (chunk: MessageInstance[]) => {
      const validationService = createMessageValidationService();
      const encoder = new PreviewMessageEncoder();
      const adapter = getGlobalVirtualMessageAdapter();
      const projectId = selectedProject?.id ?? 'demo-project';
      const environment = selectedProject?.environment ?? 'development';
      const runId = `bulk-${Date.now()}`;
      const events: MessageEvent[] = [];

      for (const message of chunk) {
        const validation = validationService.validate(messageDefinition, message.fields);
        const severity = validation.severity === 'INFO' ? 'PASS' : validation.severity;
        const enriched: MessageInstance = { ...message, projectId, environment, runId };
        const event: MessageEvent = {
          id: message.id,
          projectId,
          runId,
          messageType: message.messageType,
          standard: message.standard,
          name: message.name,
          source: message.source,
          target: message.target,
          sourceRole: message.source,
          targetRole: message.target,
          direction: message.direction,
          timestamp: message.timestamp,
          payload: message.fields,
          fields: message.fields,
          sequenceNumber: message.sequenceNumber ?? 0,
          validation: severity,
          validationStatus: severity,
          environment,
          createdAt: new Date().toISOString(),
          createdBy: userProfile?.uid ?? 'bulk',
        };
        events.push(event);
        try {
          const rawBytes = encoder.encode(enriched);
          await adapter.publish({ ...enriched, rawBytes: Array.from(rawBytes) });
        } catch {
          await adapter.publish(enriched);
        }
      }
      await Promise.all(events.map((event) => messageEventRepository.save(event).catch(() => undefined)));
    },
    [messageDefinition, selectedProject, userProfile, messageEventRepository]
  );

  const { start, cancel } = useBulkMessageWorker({
    onChunk: async (chunk) => {
      await processChunk(chunk);
    },
    onProgress: (totalGenerated) => {
      setProgress(Math.min((totalGenerated / count) * 100, 100));
      setTotal(totalGenerated);
    },
    onComplete: (totalGenerated, cancelled) => {
      setIsRunning(false);
      setProgress(cancelled ? 0 : 100);
      setTotal(totalGenerated);
      setError(cancelled ? '취소되었습니다.' : null);
      if (!cancelled) {
        setElapsedMs(performance.now() - startTimeRef.current);
      }
    },
    onError: (err) => {
      setIsRunning(false);
      setError(err);
      setProgress(0);
    },
  });

  const startTimeRef = { current: 0 };

  const handleStart = () => {
    if (!selectedProject) {
      setError('프로젝트를 선택하세요.');
      return;
    }
    if (count < 1 || count > 500000) {
      setError('1 ~ 500,000 범위 내에서 입력하세요.');
      return;
    }
    setIsRunning(true);
    setProgress(0);
    setTotal(0);
    setError(null);
    setElapsedMs(0);
    startTimeRef.current = performance.now();
    const rule = {
      id: `bulk-${Date.now()}`,
      messageType: messageDefinition.messageType,
      messageDefinitionId: messageDefinition.id,
      enabled: true,
      sourceRole: messageDefinition.sourceRole,
      targetRole: messageDefinition.targetRole,
      payloadTemplate: payload,
      fieldRules: {},
      generationMode: 'BULK' as const,
      intervalMs: 0,
      count,
      startDelayMs: 0,
      randomization: false,
      faultInjection: false,
      order: 0,
    };
    start(rule, count, defaultCoordinate, `bulk-${Date.now()}`);
  };

  const handleCancel = () => {
    cancel();
    setIsRunning(false);
  };

  return (
    <Box sx={{ mt: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle2" gutterBottom>
        대량 생성 (Web Worker)
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>개수</InputLabel>
          <Select value={count} label="개수" onChange={(e) => setCount(Number(e.target.value))}>
            <MenuItem value={1000}>1,000</MenuItem>
            <MenuItem value={10000}>10,000</MenuItem>
            <MenuItem value={50000}>50,000</MenuItem>
            <MenuItem value={100000}>100,000</MenuItem>
            <MenuItem value={250000}>250,000</MenuItem>
            <MenuItem value={500000}>500,000</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleStart} disabled={isRunning}>
          Start
        </Button>
        <Button variant="outlined" onClick={handleCancel} disabled={!isRunning}>
          Cancel
        </Button>
      </Box>
      {isRunning && <LinearProgress variant="determinate" value={progress} sx={{ mt: 2 }} />}
      {total > 0 && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
          생성: {total.toLocaleString()} / {count.toLocaleString()}
        </Typography>
      )}
      {elapsedMs > 0 && (
        <Typography variant="caption" sx={{ display: 'block' }}>
          소요: {(elapsedMs / 1000).toFixed(2)}초 / msg/sec: {Math.round(total / (elapsedMs / 1000)).toLocaleString()}
        </Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default BulkGenerator;
